/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { useAdminBooking, useUpdateAdminBookingStatus } from '../../services/adminBookingService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentApi } from '../../services/paymentService';
import PaymentHistoryModal from '../../components/client/PaymentHistoryModal';
import AdminStatusUpdateModal from '../../components/admin/StatusUpdateModal';
import EventLogs from '../../components/shared/EventLogs';
import { useEventLogs } from '../../hooks/useEventLogs';
import {
  BookingReference,
  EventDetails,
  ServicePackageDetails,
  ClientReview
} from '../../components/vendor/booking';
import {
  AdminBookingHeader,
  AdminClientInfo,
  AdminVendorInfo,
  AdminPaymentSummary
} from '../../components/admin/booking';
import { getAdminInvoice } from '../../components/admin/booking/AdminInvoice';
import BookingChatPanel from '../../components/booking/BookingChatPanel';

const AdminBookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);

  const { data: response, isLoading, error, refetch } = useAdminBooking(id);
  const booking = response?.data || response;
  
  // Fetch event logs
  const { eventLogs, isLoading: isLoadingEventLogs } = useEventLogs(id);

  // Fetch payments for this booking
  const { data: paymentsResponse, isLoading: isLoadingPayments } = useQuery({
    queryKey: ['payments', id],
    queryFn: () => paymentApi.getPaymentByBooking(id),
    enabled: !!id && !!booking,
    select: (response) => {
      // Handle different response structures
      if (Array.isArray(response)) {
        return response;
      }
      if (response?.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      return [];
    }
  });

  const payments = paymentsResponse || [];

  // Debug logging
  React.useEffect(() => {
    if (payments.length > 0) {
      console.log('Payments fetched:', payments);
    } else if (!isLoadingPayments && id) {
      console.log('No payments found for booking:', id);
    }
  }, [payments, isLoadingPayments, id]);

  // Update booking status mutation
  const updateStatusMutation = useUpdateAdminBookingStatus({
    onSuccess: () => {
      queryClient.invalidateQueries(['adminBooking', id]);
      setShowStatusModal(false);
      refetch();
    },
    onError: (error) => {
      console.error('Failed to update booking status:', error);
    }
  });

  const handleStatusUpdate = (newStatus, notes) => {
    if (newStatus && newStatus !== booking?.status) {
      updateStatusMutation.mutate({
        id,
        status: newStatus,
        notes: notes || null
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) {
        return timeString;
      }
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return timeString;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.pending;
  };

  const formatStatusText = (status) => {
    return status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Pending';
  };

  // Use AdminInvoice utility for invoice generation
  const { handleDownloadInvoice } = getAdminInvoice(booking, payments);

  // Skeleton Loading Component
  const SkeletonLoader = () => (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-2 animate-pulse">
        <div className="h-5 w-32 bg-gray-200 rounded"></div>
        <div className="bg-white border rounded-lg p-2 sm:p-3">
          <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3">
          <div className="lg:col-span-2 space-y-2 sm:space-y-3">
            <div className="bg-white border rounded-lg p-2 sm:p-3">
              <div className="h-5 w-32 bg-gray-200 rounded mb-3"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-2 sm:space-y-3">
            <div className="bg-white border rounded-lg p-2 sm:p-3">
              <div className="h-5 w-40 bg-gray-200 rounded mb-3"></div>
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-4 w-full bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6 sm:p-8">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-black mx-auto mb-4" />
            <h3 className="text-xl font-bold text-black mb-2">Booking Not Found</h3>
            <p className="text-gray-600 mb-6">
              {error?.response?.data?.message || 'The booking you are looking for does not exist.'}
            </p>
            <button
              onClick={() => navigate('/admin/bookings')}
              className="inline-flex items-center px-4 py-2 bg-black text-white rounded-md hover:bg-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Bookings
            </button>
          </div>
        </div>
      </div>
    );
  }

  const client = booking.client;
  const vendor = booking.vendor;
  const service = booking.vendor_service || booking.vendorService;
  const packageData = booking.vendor_service_package || booking.vendorServicePackage;

  // Calculate payment totals
  const completedPayments = payments.filter(p => p.status === 'completed');
  const totalPaid = completedPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const depositPaid = completedPayments.filter(p => p.payment_type === 'deposit')
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const fullPaymentPaid = completedPayments.filter(p => p.payment_type === 'full_payment').length > 0;

  // Check for reviews
  const hasReview = booking.reviews && booking.reviews.length > 0;
  const review = hasReview ? booking.reviews[0] : null;

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-2">
        {/* Header */}
        <AdminBookingHeader
          onBack={() => navigate('/admin/bookings')}
          status={booking.status}
          statusColor={getStatusColor(booking.status)}
          statusText={formatStatusText(booking.status)}
          onUpdateStatus={() => {
            if (booking.status !== 'completed') {
              setShowStatusModal(true);
            }
          }}
        />

        {/* Booking Reference */}
        <BookingReference
          bookingReference={booking.booking_reference}
          createdAt={booking.created_at}
          formatDate={formatDate}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-2 sm:space-y-3">
            {/* Event Details */}
            <EventDetails
              booking={booking}
              formatDate={formatDate}
              formatTime={formatTime}
            />

            <BookingChatPanel bookingId={id} isAdmin className="min-h-[320px]" />

            {/* Event Logs */}
            {eventLogs && eventLogs.length > 0 && (
              <EventLogs eventLogs={eventLogs} isLoading={isLoadingEventLogs} />
            )}

            {/* Service & Package Details */}
            <ServicePackageDetails
              service={service}
              packageData={packageData}
            />

            {/* Review Section */}
            <ClientReview review={review} />
          </div>

          {/* Sidebar */}
          <div className="space-y-2 sm:space-y-3">
            {/* Client Information */}
            <AdminClientInfo client={client} />

            {/* Vendor Information */}
            <AdminVendorInfo vendor={vendor} />

            {/* Payment Summary */}
            <AdminPaymentSummary
              booking={booking}
              totalPaid={totalPaid}
              depositPaid={depositPaid}
              fullPaymentPaid={fullPaymentPaid}
              payments={payments}
              onDownloadInvoice={handleDownloadInvoice}
              onViewPaymentHistory={() => setShowPaymentHistoryModal(true)}
            />
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      <AdminStatusUpdateModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={booking?.status}
        onUpdate={handleStatusUpdate}
        isUpdating={updateStatusMutation.isPending}
      />

      {/* Payment History Modal */}
      <PaymentHistoryModal
        isOpen={showPaymentHistoryModal}
        onClose={() => setShowPaymentHistoryModal(false)}
        payments={payments}
      />
    </div>
  );
};

export default AdminBookingDetails;
